// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserPlus, Mail, Shield, ChevronDown, X, Copy,
  Check, Clock, AlertTriangle, Crown, ShieldCheck, Eye, User,
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../App';

const roleBadge = {
  owner: { bg: 'bg-amber-50', text: 'text-amber-700', icon: Crown, label: 'Owner' },
  admin: { bg: 'bg-blue-50', text: 'text-blue-700', icon: ShieldCheck, label: 'Admin' },
  member: { bg: 'bg-green-50', text: 'text-green-700', icon: User, label: 'Member' },
  viewer: { bg: 'bg-gray-100', text: 'text-gray-600', icon: Eye, label: 'Viewer' },
};

const Team = () => {
  const { role: myRole, org } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' });
  const [inviteResult, setInviteResult] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [sending, setSending] = useState(false);

  const isAdmin = myRole === 'owner' || myRole === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const [membersRes, invitesRes] = await Promise.all([
        api.get('/org/members'),
        isAdmin ? api.get('/org/invitations') : Promise.resolve({ data: [] }),
      ]);
      setMembers(membersRes.data);
      setInvitations(invitesRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await api.post('/org/invitations', inviteForm);
      setInviteResult(res.data);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleRoleChange = async (membershipId, newRole) => {
    try {
      await api.put(`/org/members/${membershipId}/role`, { role: newRole });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (membershipId, name) => {
    if (window.confirm(`Remove ${name} from the organization?`)) {
      try {
        await api.delete(`/org/members/${membershipId}`);
        fetchData();
      } catch (err) {
        alert(err.response?.data?.detail || 'Failed to remove member');
      }
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await api.delete(`/org/invitations/${inviteId}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0891B2]"></div></div>;
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-sm text-gray-500 mt-1">{members.length} member{members.length !== 1 ? 's' : ''} in {org?.name || 'your organization'}</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setInviteForm({ email: '', role: 'member' }); setInviteResult(null); setShowInviteModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#0891B2] text-white text-sm font-semibold rounded-lg hover:bg-[#0e7490] transition-colors"
          >
            <UserPlus size={16} /> Invite Member
          </button>
        )}
      </div>

      {/* RBAC Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Role Permissions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(roleBadge).map(([key, val]) => (
            <div key={key} className={`p-3 rounded-lg ${val.bg} flex items-center gap-2`}>
              <val.icon size={16} className={val.text} />
              <div>
                <p className={`text-sm font-semibold ${val.text}`}>{val.label}</p>
                <p className="text-[10px] text-gray-500">
                  {key === 'owner' && 'Full access, manage org'}
                  {key === 'admin' && 'CRUD + invite members'}
                  {key === 'member' && 'Create & edit data'}
                  {key === 'viewer' && 'Read-only access'}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-xl border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Members</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full ekos-table min-w-[800px]">
            <thead>
              <tr className="bg-gray-50">
                <th>Name</th><th>Email</th><th>Role</th><th>Joined</th>{isAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const badge = roleBadge[m.role] || roleBadge.viewer;
                return (
                  <tr key={m.id}>
                    <td className="font-medium text-gray-900">{m.name}</td>
                    <td className="text-sm text-gray-500">{m.email}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${badge.bg} ${badge.text}`}>
                        <badge.icon size={12} /> {badge.label}
                      </span>
                    </td>
                    <td className="text-sm text-gray-500">{m.joined_at?.split('T')[0] || 'N/A'}</td>
                    {isAdmin && (
                      <td>
                        {m.role !== 'owner' && (
                          <div className="flex items-center gap-2">
                            <select
                              value={m.role}
                              onChange={(e) => handleRoleChange(m.id, e.target.value)}
                              className="text-xs border border-gray-200 rounded px-2 py-1 focus:ring-1 focus:ring-[#0891B2] outline-none"
                            >
                              <option value="admin">Admin</option>
                              <option value="member">Member</option>
                              <option value="viewer">Viewer</option>
                            </select>
                            <button onClick={() => handleRemoveMember(m.id, m.name)} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><X size={14} /></button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Invitations */}
      {isAdmin && invitations.filter((i) => i.status === 'pending').length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Pending Invitations</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {invitations.filter((i) => i.status === 'pending').map((inv) => (
              <div key={inv.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
                    <Mail size={14} className="text-amber-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{inv.email}</p>
                    <p className="text-xs text-gray-500">Invited as {inv.role} • {inv.created_at?.split('T')[0]}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyLink(inv.invite_url || `${window.location.origin}/invite/${inv.token}`)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#0891B2] border border-[#0891B2] rounded hover:bg-cyan-50 transition-colors"
                  >
                    <Copy size={12} /> Copy Link
                  </button>
                  <button onClick={() => handleCancelInvite(inv.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"><X size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Invite Team Member</h3>
              <button onClick={() => setShowInviteModal(false)} className="p-1 hover:bg-gray-100 rounded"><X size={20} className="text-gray-400" /></button>
            </div>

            {!inviteResult ? (
              <form onSubmit={handleInvite} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email" value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none"
                    placeholder="colleague@company.com" required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0891B2] focus:border-transparent outline-none"
                  >
                    <option value="admin">Admin — Full CRUD + invite members</option>
                    <option value="member">Member — Create & edit data</option>
                    <option value="viewer">Viewer — Read-only access</option>
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setShowInviteModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                  <button type="submit" disabled={sending} className="px-4 py-2 text-sm font-semibold text-white bg-[#0891B2] rounded-lg hover:bg-[#0e7490] disabled:opacity-60">{sending ? 'Sending...' : 'Send Invitation'}</button>
                </div>
              </form>
            ) : (
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                  <Check size={20} className="text-green-600" />
                  <div>
                    <p className="text-sm font-semibold text-green-800">Invitation sent!</p>
                    <p className="text-xs text-green-600">An invitation has been created for {inviteResult.email}</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Share this invite link</label>
                  <div className="flex gap-2">
                    <input
                      type="text" readOnly
                      value={inviteResult.invite_url || `${window.location.origin}/invite/${inviteResult.token}`}
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600"
                    />
                    <button
                      onClick={() => copyLink(inviteResult.invite_url || `${window.location.origin}/invite/${inviteResult.token}`)}
                      className="px-3 py-2 bg-[#0891B2] text-white text-xs font-semibold rounded-lg hover:bg-[#0e7490]"
                    >
                      {copiedLink ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                  </div>
                </div>
                <button onClick={() => { setShowInviteModal(false); setInviteResult(null); }} className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Done</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Team;
